import { isEmpty } from "ramda";
import { useMemo, useState } from "react";
import { Button, DropdownSelect, Loading, Modal, Spinner, TextInput, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import { useCreateVehicleMutation } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast } from "@/utils";
import { ConfirmRegistrationChangeModal } from "./ConfirmRegistrationChangeModal";
import { PlateAlreadyLinkedModal } from "./PlateAlreadyLinkedModal";

interface ChangeVehicleStateModalProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
}

export const ChangeVehicleStateModal = ({ vehicle, open, onClose }: ChangeVehicleStateModalProps) => {
  const { country } = useOperator();
  const { isFetching, data: response } = useGetCountryStatesQuery(country);
  const [checkVehicle, { isLoading }] = useCreateVehicleMutation();
  const [state, setState] = useState(vehicle.state.uuid);
  const [numberPlate, setNumberPlate] = useState(vehicle.registrationNumber);
  const [step, setStep] = useState<"input" | "confirm" | "plate-linked">("input");
  const [existingVehicle, setExistingVehicle] = useState<Record<string, unknown> | undefined>();
  const [lookupVehicle, setLookupVehicle] = useState<Vehicle | undefined>();

  const options = useMemo(
    () => response?.data.states.map((s) => ({ name: s.name, value: s.id })) ?? [],
    [response?.data.states]
  );

  const selectedStateAbbreviation = useMemo(() => {
    const found = response?.data.states.find((s) => s.id === state);
    return found?.abbreviation ?? "";
  }, [response?.data.states, state]);

  const handleClose = () => {
    setState(vehicle.state.uuid);
    setNumberPlate(vehicle.registrationNumber);
    setStep("input");
    setExistingVehicle(undefined);
    setLookupVehicle(undefined);
    onClose();
  };

  const handleCheckVehicle = () => {
    if (!state || !numberPlate.trim()) return;

    // Dry-run to validate the plate+state without actually changing anything
    checkVehicle({
      dry_run: true,
      registration_number: numberPlate.trim(),
      state_uuid: state,
    })
      .unwrap()
      .then((result) => {
        if (result.vin && vehicle.vin && result.vin !== vehicle.vin) {
          addToast(
            "danger",
            "Unable to change: VIN for this plate belongs to a different vehicle. Please archive this vehicle and add the new vehicle instead."
          );
          return;
        }
        setLookupVehicle(result);
        setStep("confirm");
      })
      .catch((e) => {
        const errorData = (e as { data?: { error_data?: { existing_vehicle?: Record<string, unknown>; vehicle_uuid?: string; is_archived?: boolean } } })?.data?.error_data;
        if (errorData?.existing_vehicle) {
          setExistingVehicle(errorData.existing_vehicle);
          setStep("plate-linked");
        } else if (errorData?.vehicle_uuid && !errorData?.is_archived) {
          addToast("danger", "This plate number is already registered to another vehicle");
        } else {
          addToast("danger", "Vehicle lookup failed. Please check the details and try again.");
        }
      });
  };

  if (step === "confirm") {
    return (
      <ConfirmRegistrationChangeModal
        vehicle={vehicle}
        newPlate={numberPlate.trim()}
        newStateUuid={state}
        lookupVehicle={lookupVehicle}
        open={open}
        onClose={handleClose}
      />
    );
  }

  if (step === "plate-linked") {
    return (
      <PlateAlreadyLinkedModal
        open={open}
        existingVehicle={existingVehicle as never}
        onClose={handleClose}
      />
    );
  }

  return (
    <Modal open={open} onClose={handleClose} className="w-[85vw] !p-6 md:w-[400px] lg:w-[400px]">
      <div className="flex min-h-[500px] flex-col">
        <Typography variant="title">Update Vehicle State</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          Select the new state below
        </Typography>

        <div className="relative mt-5 flex flex-1 flex-col">
          {isLoading && <Loading />}

          <div className="flex flex-col gap-1">
            <Typography variant="paragraph">New State</Typography>
            <DropdownSelect
              className="w-full"
              placeholder="Select State"
              options={options}
              value={state}
              onChange={setState}
              disabled={isEmpty(options) || isFetching}
            />
          </div>

          <label className="mt-5 flex flex-col gap-1">
            <Typography variant="paragraph">
              {selectedStateAbbreviation ? `${selectedStateAbbreviation} Vehicle Plate Number` : "Vehicle Plate Number"}
            </Typography>
            <TextInput
              placeholder={selectedStateAbbreviation ? `Enter ${selectedStateAbbreviation} Vehicle Plate Number` : "Enter Vehicle Plate Number"}
              value={numberPlate}
              onChange={(e) => setNumberPlate(e.target.value)}
            />
          </label>

          <div className="mt-auto flex flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleCheckVehicle}
              disabled={isLoading || !state || !numberPlate.trim()}
            >
              {isLoading ? <Spinner /> : "Check Vehicle"}
            </Button>
            <Button variant="secondary" size="lg" className="w-full" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
